import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import SelectMultipleChip from "../select-multiple";
import gql from "graphql-tag";
import { useQuery, QueryResponseHandler } from "components/graphql";
import Select from "../select";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { useAuthDataContext } from "components/auth";
import { DEFAULT_SELECT_LIMIT } from "../utils";
import { injectIntl } from "react-intl";

const getQuery = ({
  queryString,
  customerId,
  hasAdminPrivileges,
  isPartner,
  username,
  isFirstRender,
  limit,
  variables,
}) => ({
  query: gql`
    ${queryString}
  `,
  skip: isFirstRender || (!customerId && !hasAdminPrivileges && !isPartner),
  variables: { offset: 0, limit: limit, customerId, username, ...variables },
});

const removeExclusions = (items, excluded, value) => {
  return excluded ? items.filter(i => !excluded.includes(i.id) || i.id === value) : items;
};

const getItems = elements => (Array.isArray(elements) ? elements : elements.items);

const SimpleEntitiesSelector = ({
  queryString,
  offset = 0,
  limit = DEFAULT_SELECT_LIMIT,
  onGetDataFromResponse,
  multiple = false,
  onChange,
  value = multiple ? [] : "",
  onGetValue,
  onGetLabel,
  manuallyScopedQuery,
  excluded = [],
  setFirstIfOnlyOneValue = false,
  emptyValueText,
  variables,
  onQueryDone,
  customerIdSelected,
  getMultipleDefaultValue,
  intl,
  forceRefetch,
  currentValue,
  ...props
}) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const { user, hasAdminPrivileges, isPartner } = useAuthDataContext();
  const { currentCustomerId } = useCurrentCustomerContext();

  const SelectorComponent = multiple ? SelectMultipleChip : Select;
  const handleChange = ({ target: { value } }) => onChange({ id: value });
  const handleMultipleChange = ({ target: { value } }) => onChange(value);


  const customerIdForQuery = hasAdminPrivileges && !manuallyScopedQuery ? null : currentCustomerId;
  const query = useMemo(
    () =>
      getQuery({
        limit,
        queryString,
        customerId: customerIdSelected ? customerIdSelected : customerIdForQuery,
        hasAdminPrivileges,
        isPartner,
        username: user.sub,
        isFirstRender,
        variables,
      }),
    [
      limit,
      queryString,
      customerIdSelected,
      customerIdForQuery,
      hasAdminPrivileges,
      isPartner,
      user.sub,
      isFirstRender,
      variables,
    ],
  );

  const { data, loading, error, refetch } = useQuery(query);
  const elements = onGetDataFromResponse(data);

  const refreshSelect = useCallback(
      () => {
        refetch();
      },
      [refetch],
  );

  useEffect(
    () => {
      if (!loading && onQueryDone) {
        onQueryDone(elements);
      }
    },
    [elements, loading, onQueryDone],
  );

  useEffect(() => {
    if (forceRefetch){
      refreshSelect();
      handleChange({ target: { value: currentValue } });
    }
  },[forceRefetch])

  const getValueFromElement = useCallback(element => onGetValue(element), [onGetValue]);
  const getLabelFromElement = useCallback(element => onGetLabel(element), [onGetLabel]);

  const setFirstValueByDefault = setFirstIfOnlyOneValue && elements && elements.total === 1;
  let defaultSelectedValue = value;

  if (!multiple && setFirstValueByDefault) {
    defaultSelectedValue = getValueFromElement(elements.items[0]);
  }

  if (multiple && getMultipleDefaultValue && !value && elements) {
    defaultSelectedValue = getMultipleDefaultValue(elements);
  }

  useEffect(
    () => {
      if (value !== defaultSelectedValue) {
        onChange(multiple ? defaultSelectedValue : { id: defaultSelectedValue });
      }
    },
    [defaultSelectedValue, multiple, onChange, props.id, value],
  );

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  const commonProps = {
    value,
    onChange: !multiple ? handleChange : handleMultipleChange,
    getValueFromElement,
    getLabelFromElement,
    elements: elements ? removeExclusions(getItems(elements), excluded, value) : [],
    ...props,
  };

  const singleOptionProps = {
    emptyValueText: setFirstValueByDefault ? null : emptyValueText,
  };

  const multipleOptionProps = {};

  const selectProps = {
    ...commonProps,
    ...(multiple ? multipleOptionProps : singleOptionProps),
  };
  const assetNameLanguage = intl.formatMessage({ id: "asset.noselected" });

    useEffect(() => {
    if (elements && elements.__typename === 'AssetsWithTotal') {
      if (elements?.items?.length !== elements?.total + 1) {
            elements.items.unshift({id:'',codename: `${assetNameLanguage}`,__typename: null});
        }
    }
    },[elements]);


  return (
    <QueryResponseHandler error={error} loading={loading} elements={elements}>
      <SelectorComponent {...selectProps} />
    </QueryResponseHandler>
  );
};

SimpleEntitiesSelector.propTypes = {
  /**
   * Indica si la query de superadmin se ejecuta en un contexto de selección manual del customer
   **/
  manuallyScopedQuery: PropTypes.bool,
  /**
   * En caso de selector no múltiple, representa el valor del texto
   * de la opción vacía, si existe (opcional)
   */
  emptyValueText: PropTypes.string,
  /**
   * Query GraphQL
   */
  queryString: PropTypes.string.isRequired,
  /**
   * Offset enviado a la query GraphQL paginada
   */
  offset: PropTypes.number,
  /**
   * Limit enviado a la query GraphQL paginada
   */
  limit: PropTypes.number,
  /**
   * Función que recupera el objeto de datos paginados a mostrar a partir del resultado de la query
   */
  onGetDataFromResponse: PropTypes.func.isRequired,
  /**
   * Indica si el selector es múltiple o no
   */
  multiple: PropTypes.bool,
  /**
   * Indica si se selecciona por defecto el primer valor si solamente hay uno
   */
  setFirstIfOnlyOneValue: PropTypes.bool,
  /**
   * Etiqueta opcional para el campo
   */
  label: PropTypes.string,
  /**
   * Id del input
   */
  id: PropTypes.string,
  /**
   * Nombre del input
   */
  name: PropTypes.string,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Customer id que se utilizará en la query
   */
  customerIdSelected: PropTypes.string,
  /**
   * Función que se ejecuta al cambiar el valor del campo
   */
  onChange: PropTypes.func,
  /**
   * Valor actual del selector
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Función que obtiene el valor de la opción del selector a partir de un elemento
   */
  onGetValue: PropTypes.func.isRequired,
  /**
   * Función que obtiene el label de la opción del selector a partir de un elemento
   */
  onGetLabel: PropTypes.func.isRequired,
  /**
   * Callback que devuelve los elementos de la query
   */
  onQueryDone: PropTypes.func,
  /**
   * Valores a excluir en el selector
   */
  excluded: PropTypes.array,
  /**
   * Variables de la query a sobreescribir
   */
  variables: PropTypes.object,
  /**
   * Función que obtiene el valor por defecto para los selectores múltiples
   */
  getMultipleDefaultValue: PropTypes.func,
};

export default injectIntl(SimpleEntitiesSelector);
